import type { ColumnGroup, EmptyStateConfigProps } from '@commenda/kakashi';
import { Alert, Button, DataTable, IconButton, Icons } from '@commenda/kakashi';
import { AgentConversationStatus, IAgentConversation } from '@commenda/types';
import { formatRelativeTime } from '@commenda/utils';
import React from 'react';

interface ConversationListViewProps {
  conversations: IAgentConversation[];
  onSelect: (conversation: IAgentConversation) => void;
  onNewChat: () => void;
  onDelete: (conversation: IAgentConversation) => void;
  isLoading?: boolean;
  /** A failed list read; shown in place of the table so it never reads as an empty list. */
  error?: unknown;
  title?: string;
  headerLabel?: string;
  newChatLabel?: string;
  searchPlaceholder?: string;
  emptyStateConfig?: EmptyStateConfigProps;
  /** Optional action rendered in the header next to the primary "new" button. */
  headerAction?: React.ReactNode;
}

const STATUS_MAP: Record<
  AgentConversationStatus,
  { label: string; className: string }
> = {
  ACTIVE: { label: 'Active', className: 'bg-green-500' },
  COMPLETED: { label: 'Completed', className: 'bg-gray-400' },
  FAILED: { label: 'Failed', className: 'bg-red-500' },
};

function buildColumnGroups(
  onDelete: (conversation: IAgentConversation) => void
): ColumnGroup<IAgentConversation, unknown>[] {
  return [
    {
      id: 'conversation-details',
      columns: [
        {
          id: 'title',
          accessorFn: (row: IAgentConversation) =>
            row.title ?? 'Untitled conversation',
          header: 'Title',
          title: 'Title',
          cell: ({ row }: { row: { original: IAgentConversation } }) => (
            <span className="txt-small-medium text-text-primary truncate block max-w-md">
              {row.original.title ?? 'Untitled conversation'}
            </span>
          ),
        },
        {
          id: 'createdByName',
          accessorFn: (row: IAgentConversation) =>
            row.createdByName ?? 'Unknown',
          header: 'Created By',
          title: 'Created By',
          cell: ({ row }: { row: { original: IAgentConversation } }) => (
            <span className="txt-small-regular text-text-secondary">
              {row.original.createdByName ?? 'Unknown'}
            </span>
          ),
        },
        {
          id: 'status',
          accessorFn: (row: IAgentConversation) => row.status,
          header: 'Status',
          title: 'Status',
          cell: ({ row }: { row: { original: IAgentConversation } }) => {
            const entry = STATUS_MAP[row.original.status];
            return (
              <span className="inline-flex items-center gap-1.5 txt-small-regular text-text-secondary">
                <span
                  className={`w-1.5 h-1.5 rounded-full ${entry.className}`}
                />
                {entry.label}
              </span>
            );
          },
        },
        {
          id: 'updatedAt',
          accessorFn: (row: IAgentConversation) => row.updatedAt,
          header: 'Last Updated',
          title: 'Last Updated',
          cell: ({ row }: { row: { original: IAgentConversation } }) => (
            <span className="txt-small-regular text-text-tertiary">
              {formatRelativeTime(row.original.updatedAt)}
            </span>
          ),
        },
        {
          id: 'actions',
          accessorFn: () => null,
          header: '',
          title: '',
          showInSelector: false,
          cell: ({ row }: { row: { original: IAgentConversation } }) => (
            <IconButton
              icon={<Icons.Trash01 />}
              size="sm"
              aria-label="Delete conversation"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onDelete(row.original);
              }}
            />
          ),
        },
      ],
    },
  ];
}

export function ConversationListView({
  conversations,
  onSelect,
  onNewChat,
  onDelete,
  isLoading,
  error,
  title = 'Agent',
  headerLabel = 'Tools',
  newChatLabel = 'New chat',
  searchPlaceholder = 'Search conversations…',
  emptyStateConfig = {
    title: 'No conversations yet',
    description: 'Start a new chat to create your first conversation.',
  },
  headerAction,
}: ConversationListViewProps) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-end justify-between p-6">
        <div className="flex flex-col gap-2">
          <span className="txt-extra-small-bold text-fg-brand">
            {headerLabel}
          </span>
          <h1 className="txt-display-large-bold text-text-primary">{title}</h1>
        </div>
        <div className="flex items-center gap-md">
          {headerAction}
          <Button onClick={onNewChat} size="md">
            <Icons.Plus className="size-4" />
            {newChatLabel}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 pt-2">
        {error ? (
          <Alert
            variant="destructive"
            description="Couldn't load conversations. Refresh to try again."
          />
        ) : (
          <DataTable
            data={conversations}
            columnGroups={buildColumnGroups(onDelete)}
            isLoading={isLoading}
            onRowClick={(row) => onSelect(row as IAgentConversation)}
            searchConfig={{
              isEnabled: true,
              columnsToEnableSearchFor: ['title'],
              placeholder: searchPlaceholder,
            }}
            emptyStateConfig={emptyStateConfig}
            tableContainerClassName="border-0"
            tableWrapperClassName="mb-0"
            tableCellClassName="border-0 h-14"
            tableHeadClassName="txt-extra-small-medium text-muted-foreground"
          />
        )}
      </div>
    </div>
  );
}
